import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SenseSphereOAuthService, SenseSphereUserInfo, SenseSphereSystemInfo } from 'src/shared/services/sensesphere-oauth.service';

@Component({
    selector: 'app-sensesphere-callback',
    template: `
        <div class="flex flex-col justify-center items-center h-screen" style="background: linear-gradient(135deg, #0f1429 0%, #1a1f36 50%, #1e2748 100%);">
            <!-- Loading state -->
            <ng-container *ngIf="isLoading; else loadedTemplate">
                <div class="text-center">
                    <div class="spinner mb-6"></div>
                    <h2 class="text-xl font-semibold" style="color: #e8ecf4;">Đang xác thực...</h2>
                    <p style="color: rgba(190, 197, 215, 0.7);">Vui lòng đợi trong giây lát</p>
                </div>
            </ng-container>

            <!-- Success/Error state -->
            <ng-template #loadedTemplate>
                <ng-container *ngIf="!error; else errorTemplate">
                    <div class="card-container animate-cardFadeIn">
                        <!-- Animated Checkmark -->
                        <div class="checkmark-wrapper mb-6">
                            <svg class="checkmark-svg" viewBox="0 0 52 52">
                                <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                                <path class="checkmark-path" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                            </svg>
                        </div>

                        <!-- Avatar -->
                        <img 
                            *ngIf="getAvatarUrl()" 
                            [src]="getAvatarUrl()!" 
                            alt="Avatar"
                            class="avatar-image animate-avatarPulse"
                            style="border: 3px solid rgba(41, 121, 255, 0.5);">
                        <div *ngIf="!userInfo?.avatar" class="avatar-initial animate-avatarPulse" style="background: linear-gradient(135deg, #2979ff 0%, #1e88e5 100%);">
                            {{ userInfo?.full_name?.charAt(0)?.toUpperCase() || 'U' }}
                        </div>

                        <!-- User Info -->
                        <h2 class="user-name" style="color: #ffffff;">{{ userInfo?.full_name || userInfo?.username }}</h2>
                        <p *ngIf="userInfo?.email" class="user-email" style="color: rgba(190, 197, 215, 0.6);">{{ userInfo?.email }}</p>

                        <!-- Success Message -->
                        <div class="success-message">
                            <svg class="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span style="color: #10b981;">Đăng nhập thành công!</span>
                        </div>

                        <!-- Progress Bar -->
                        <div class="progress-container">
                            <div class="progress-track">
                                <div class="progress-fill"></div>
                            </div>
                            <p style="color: rgba(190, 197, 215, 0.5); font-size: 12px; margin-top: 8px;">Chuyển hướng đến bảng điều khiển...</p>
                        </div>
                    </div>
                </ng-container>
            </ng-template>

            <!-- Error state -->
            <ng-template #errorTemplate>
                <div class="text-center animate-fadeIn max-w-md px-6">
                    <div class="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-6 mx-auto" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 class="text-xl font-semibold mb-2" style="color: #ffffff;">Đăng nhập thất bại</h2>
                    <p style="color: rgba(190, 197, 215, 0.8); font-size: 14px; line-height: 1.6; margin-bottom: 24px;">
                        {{ error }}
                    </p>
                    <button 
                        (click)="onRetry()"
                        class="px-6 py-3 rounded-lg font-semibold text-white cursor-pointer transition-all"
                        style="background: linear-gradient(135deg, #2979ff 0%, #1e88e5 100%); box-shadow: 0 2px 8px rgba(41, 121, 255, 0.4);">
                        <span class="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Thử lại
                        </span>
                    </button>
                </div>
            </ng-template>
        </div>
    `,
    styles: [`
        .spinner {
            width: 48px;
            height: 48px;
            border: 3px solid rgba(0, 0, 0, 0.1);
            border-top-color: #2979ff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }
        @keyframes spin {
            to { transform: rotate(360deg); }
        }

        /* Card container */
        .card-container {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(16px);
            border-radius: 1rem;
            padding: 2.5rem 2rem;
            max-width: 22rem;
            width: 100%;
            margin: 0 1rem;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.08);
            text-align: center;
        }

        /* Card fade in animation */
        @keyframes cardFadeIn {
            from { opacity: 0; transform: translateY(20px) scale(0.96); }
            to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-cardFadeIn {
            animation: cardFadeIn 0.5s ease-out forwards;
        }

        /* Checkmark animations */
        .checkmark-wrapper {
            width: 64px;
            height: 64px;
            margin: 0 auto 1.5rem;
        }
        .checkmark-svg {
            width: 100%;
            height: 100%;
        }
        .checkmark-circle {
            stroke: #10b981;
            stroke-width: 2;
            fill: none;
            stroke-dasharray: 166;
            stroke-dashoffset: 166;
            animation: checkmarkCircleStroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) 0.2s forwards;
        }
        @keyframes checkmarkCircleStroke {
            100% { stroke-dashoffset: 0; }
        }
        .checkmark-path {
            stroke: #10b981;
            stroke-width: 3;
            stroke-linecap: round;
            stroke-linejoin: round;
            fill: none;
            stroke-dasharray: 48;
            stroke-dashoffset: 48;
            animation: checkmarkPathStroke 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.7s forwards;
        }
        @keyframes checkmarkPathStroke {
            100% { stroke-dashoffset: 0; }
        }

        /* Avatar styles */
        .avatar-image {
            width: 5rem;
            height: 5rem;
            border-radius: 9999px;
            object-fit: cover;
            margin: 0 auto 1.5rem;
            display: block;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        .avatar-initial {
            width: 5rem;
            height: 5rem;
            border-radius: 9999px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            color: white;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0 auto 1.5rem;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        @keyframes avatarPulse {
            0%, 100% { box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(41, 121, 255, 0.4); }
            50% { box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 8px rgba(41, 121, 255, 0); }
        }
        .animate-avatarPulse {
            animation: avatarPulse 1s ease-in-out 1s 1;
        }

        /* User info */
        .user-name {
            font-size: 1.25rem;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.25rem;
        }
        .user-email {
            font-size: 0.875rem;
            color: rgba(190, 197, 215, 0.6);
            margin-bottom: 1rem;
        }

        /* Success message */
        .success-message {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            font-size: 0.8125rem;
            font-weight: 500;
            margin-bottom: 1.5rem;
        }
        .success-icon {
            width: 16px;
            height: 16px;
            flex-shrink: 0;
        }

        /* Progress bar */
        .progress-container {
            width: 100%;
        }
        .progress-track {
            width: 100%;
            height: 4px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 9999px;
            overflow: hidden;
        }
        .progress-fill {
            width: 0%;
            height: 100%;
            background: linear-gradient(90deg, #2979ff 0%, #1e88e5 100%);
            border-radius: 9999px;
            animation: progressFill 2s linear forwards;
        }
        @keyframes progressFill {
            to { width: 100%; }
        }

        /* Original fadeIn for error state */
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
            animation: fadeIn 0.4s ease-out;
        }
    `],
})
export class SenseSphereCallbackComponent implements OnInit, OnDestroy {
    isLoading = true;
    userInfo: SenseSphereUserInfo | null = null;
    error: string | null = null;

    getAvatarUrl(): string | null {
        return this.userInfo?.avatar || null;
    }

    constructor(
        private oauthService: SenseSphereOAuthService,
        private router: Router,
        private toastService: ToastrService
    ) {}

    private redirectTimer: any;

    ngOnInit(): void {
        const result = this.oauthService.handleCallback();
        
        if (result) {
            let fetchFailed = false;
            
            this.oauthService.fetchUserInfo(result.token, result.userId).then(userInfo => {
                this.userInfo = userInfo;
                
                if (userInfo) {
                    this.toastService.success(
                        `Chào mừng ${userInfo.full_name || userInfo.username}!`, 
                        'Đăng nhập thành công'
                    );
                } else {
                    fetchFailed = true;
                    this.toastService.warning(
                        'Không thể tải thông tin người dùng. Chuyển đến trang chủ...', 
                        'Cảnh báo'
                    );
                }
                
                this.isLoading = false;
            }).catch(() => {
                fetchFailed = true;
                this.isLoading = false;
            });

            this.redirectTimer = setTimeout(() => {
                if (fetchFailed) {
                    this.toastService.error(
                        'Lỗi khi lấy thông tin người dùng. Vui lòng thử lại.', 
                        'Lỗi'
                    );
                }
                this.router.navigate(['/chatbot/icd-10']);
            }, 2000);
        } else {
            // Check for error params from SenseSphere (e.g., ?error=invalid_request)
            let errorMessage = 'Đăng nhập thất bại. Vui lòng thử lại.';

            let params: URLSearchParams;
            params = new URLSearchParams(window.location.search);

            const sphereError = params.get('error');
            const sphereErrorDesc = params.get('error_description');

            if (sphereErrorDesc) {
                errorMessage = sphereErrorDesc;
            } else if (sphereError) {
                // Map các lỗi OAuth phổ biến từ SenseSphere
                const errorMessages: Record<string, string> = {
                    'invalid_request': 'Yêu cầu không hợp lệ. Vui lòng kiểm tra cấu hình đăng nhập.',
                    'access_denied': 'Bạn đã từ chối quyền truy cập. Vui lòng thử lại.',
                    'unauthorized_client': 'Ứng dụng chưa được cấp phép. Liên hệ quản trị viên.',
                    'unsupported_response_type': 'Loại phản hồi không được hỗ trợ. Liên hệ quản trị viên.',
                    'server_error': 'Lỗi máy chủ. Vui lòng thử lại sau.',
                    'temporarily_unavailable': 'Dịch vụ tạm thời không khả dụng. Vui lòng thử lại sau.'
                };
                errorMessage = errorMessages[sphereError] || `Lỗi: ${sphereError}`;
            }

            this.error = errorMessage;
            this.isLoading = false;
        }
    }

    onRetry(): void {
        this.router.navigate(['/chatbot/icd-10']);
    }

    ngOnDestroy(): void {
        if (this.redirectTimer) {
            clearTimeout(this.redirectTimer);
        }
    }
}
